import React, { useState, useMemo, useRef } from 'react';
import { AlertCircle, Play, ChevronLeft, ChevronRight, Zap, Maximize2, Minimize2 } from 'lucide-react';
import { extractVideoId } from '../services/youtubeService';
import { useTouchFeedback } from '../hooks/useTouchFeedback';

interface VideoPlayerProps {
  article: any;
  isDark: boolean;
}

interface VideoInfo {
  id: string;
  label: string;
  type: 'conceptual' | 'popular' | 'recent';
  badge: string;
  badgeColor: string;
}

const buildVideoInfo = (article: any): VideoInfo[] => {
  const videos: VideoInfo[] = [];

  // Priority: First video (best match) - shown by default
  const conceptualId = article?.resources?.video?.conceptual;
  if (conceptualId) {
    videos.push({
      id: conceptualId,
      label: 'Best Conceptual Match',
      type: 'conceptual',
      badge: '1️⃣ First',
      badgeColor: 'from-blue-600 to-blue-500'
    });
  }

  // Second video (popularity + concept combined)
  const popularId = article?.resources?.video?.popular;
  if (popularId) {
    videos.push({
      id: popularId,
      label: 'Second Best Match',
      type: 'popular',
      badge: '2️⃣ Second',
      badgeColor: 'from-orange-600 to-orange-500'
    });
  }

  // Third video (third best)
  const recentId = article?.resources?.video?.recent;
  if (recentId) {
    videos.push({
      id: recentId,
      label: 'Third Best Match',
      type: 'recent',
      badge: '3️⃣ Third',
      badgeColor: 'from-green-600 to-green-500'
    });
  }

  // Fallback to primary video ID (for backwards compatibility)
  if (videos.length === 0) {
    const primaryId = article?.resources?.video?.id;
    if (primaryId) {
      videos.push({
        id: primaryId,
        label: 'Video Tutorial',
        type: 'conceptual',
        badge: '1️⃣ First',
        badgeColor: 'from-red-600 to-red-500'
      });
    }
  }

  return videos;
};

const buildEmbedUrl = (videoId: string, article: any): string => {
  const params = new URLSearchParams({
    autoplay: '0',
    controls: '1',
    rel: '0',
    modestbranding: '1',
  });

  const start = article?.resources?.video?.start;
  if (typeof start === 'number' && start >= 0) {
    params.set('start', String(Math.floor(start)));
  }

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ article, isDark }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [embedBlocked, setEmbedBlocked] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const { triggerHaptic } = useTouchFeedback({ enableHaptics: true, feedbackType: 'light' });

  const videos = useMemo(() => buildVideoInfo(article), [article]);

  if (videos.length === 0) {
    return (
      <div className={`rounded-2xl border p-6 flex items-start gap-3 ${
        isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
      }`}>
        <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
          isDark ? 'text-red-400' : 'text-red-600'
        }`} />
        <div>
          <p className={`font-bold text-lg ${isDark ? 'text-red-300' : 'text-red-800'}`}>
            No video available
          </p>
          <p className={isDark ? 'text-red-200' : 'text-red-700'}>
            Article does not have cached videos. Videos should be fetched when article is saved.
          </p>
        </div>
      </div>
    );
  }

  const currentVideo = videos[currentIndex];
  const embedUrl = buildEmbedUrl(currentVideo.id, article);
  const hasMultipleVideos = videos.length > 1;

  const handlePrevious = () => {
    triggerHaptic('light');
    setCurrentIndex((prev) => (prev === 0 ? videos.length - 1 : prev - 1));
    setEmbedBlocked(false);
  };

  const handleNext = () => {
    triggerHaptic('light');
    setCurrentIndex((prev) => (prev === videos.length - 1 ? 0 : prev + 1));
    setEmbedBlocked(false);
  };

  const toggleFullscreen = () => {
    triggerHaptic('medium');
    if (!isFullscreen) {
      // Enter fullscreen
      if (videoContainerRef.current?.requestFullscreen) {
        videoContainerRef.current.requestFullscreen().catch(() => {
          setIsFullscreen(!isFullscreen);
        });
      } else {
        setIsFullscreen(true);
      }
    } else {
      // Exit fullscreen
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with title and badge */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1">
          <Play className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className="flex-1">
            <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              Video Tutorial
            </h2>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              {currentVideo.label}
            </p>
          </div>
        </div>

        {/* Badge with video type */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold whitespace-nowrap shadow-md ${
          isDark 
            ? `bg-gradient-to-r ${currentVideo.badgeColor} text-white border border-opacity-50` 
            : `bg-gradient-to-r ${currentVideo.badgeColor} text-white border border-opacity-30`
        }`}>
          <span className={`w-2 h-2 rounded-full animate-pulse ${isDark ? 'bg-white/70' : 'bg-white'}`}></span>
          {currentVideo.badge}
          {hasMultipleVideos && (
            <>
              <span className={`w-0.5 h-3 mx-1 ${isDark ? 'bg-white/30' : 'bg-white/50'}`}></span>
              <span>{currentIndex + 1}/{videos.length}</span>
            </>
          )}
        </div>
      </div>

      {/* Video container with navigation */}
      <div className="relative group">
        {/* Main video player */}
        <div 
          ref={videoContainerRef}
          className={`relative bg-black rounded-xl overflow-hidden transition-all duration-300 ${
            isFullscreen 
              ? 'fixed inset-0 z-50 rounded-none' 
              : ''
          }`} 
          style={{ 
            aspectRatio: isFullscreen ? 'auto' : '16 / 9'
          }}
        >
          <iframe
            width="100%"
            height="100%"
            src={embedUrl}
            title={`${currentVideo.label} - ${article?.title || 'Video tutorial'}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ position: 'absolute', inset: 0 }}
            onError={() => setEmbedBlocked(true)}
          />

          {/* Ad Free Study Badge */}
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 z-20">
            <div className="relative">
              {/* Animated background glow - smaller on mobile */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 rounded-full blur-lg opacity-60 animate-pulse scale-75 sm:scale-100"></div>
              
              {/* Badge container - much smaller on mobile */}
              <div className="relative px-2 py-1 sm:px-4 sm:py-2 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full shadow-lg sm:shadow-2xl border border-emerald-300/50 backdrop-blur-sm flex items-center gap-1 sm:gap-1.5 animate-bounce" style={{ animationDuration: '2s' }}>
                <Zap className="w-2.5 h-2.5 sm:w-4 sm:h-4 text-white fill-white animate-pulse flex-shrink-0" style={{ animationDuration: '1.5s' }} />
                <span className="text-[10px] sm:text-sm font-bold text-white tracking-tight sm:tracking-wide whitespace-nowrap">Ad Free</span>
              </div>
            </div>
          </div>

          {/* Fullscreen button - mobile friendly */}
          <button
            onClick={toggleFullscreen}
            className={`absolute bottom-2 right-2 sm:bottom-3 sm:right-3 z-20 p-2 sm:p-2.5 rounded-lg transition-all duration-200 bg-black/50 hover:bg-black/70 text-white hover:scale-105 active:scale-95 touch-target btn-touch ${
              isDark
                ? 'hover:bg-black/80 text-white/80 hover:text-white'
                : 'hover:bg-black/70 text-white/80 hover:text-white'
            }`}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onTouchStart={() => triggerHaptic('medium')}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" />
            ) : (
              <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </button>
        </div>

        {/* Navigation arrows - only show if multiple videos */}
        {hasMultipleVideos && (
          <>
            {/* Previous button - larger touch target on mobile */}
            <button
              onClick={handlePrevious}
              className={`absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-10 p-3 sm:p-3 rounded-full transition-all duration-200 bg-black/40 hover:bg-black/60 text-white hover:scale-110 active:scale-95 opacity-70 group-hover:opacity-100 touch-target btn-touch ${
                isDark
                  ? 'hover:bg-black/80 text-white/80 hover:text-white'
                  : 'hover:bg-black/60 text-white/70 hover:text-white'
              }`}
              aria-label="Previous video"
              title={`Previous: ${videos[(currentIndex - 1 + videos.length) % videos.length].label}`}
              onTouchStart={() => triggerHaptic('light')}
            >
              <ChevronLeft className="w-5 sm:w-6 h-5 sm:h-6" />
            </button>

            {/* Next button - larger touch target on mobile */}
            <button
              onClick={handleNext}
              className={`absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-10 p-3 sm:p-3 rounded-full transition-all duration-200 bg-black/40 hover:bg-black/60 text-white hover:scale-110 active:scale-95 opacity-70 group-hover:opacity-100 touch-target btn-touch ${
                isDark
                  ? 'hover:bg-black/80 text-white/80 hover:text-white'
                  : 'hover:bg-black/60 text-white/70 hover:text-white'
              }`}
              aria-label="Next video"
              title={`Next: ${videos[(currentIndex + 1) % videos.length].label}`}
              onTouchStart={() => triggerHaptic('light')}
            >
              <ChevronRight className="w-5 sm:w-6 h-5 sm:h-6" />
            </button>
          </>
        )}

        {/* Embed blocked warning */}
        {embedBlocked && (
          <div className={`absolute inset-0 flex items-center justify-center rounded-xl backdrop-blur-sm ${
            isDark ? 'bg-black/70' : 'bg-black/50'
          }`}>
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-2" />
              <p className="text-white font-semibold mb-3">Can't play in embed</p>
              <a
                href={`https://www.youtube.com/watch?v=${currentVideo.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Open on YouTube
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Video indicators - dots showing current video */}
      {hasMultipleVideos && (
        <div className="flex items-center justify-center gap-2">
          {videos.map((video, index) => (
            <button
              key={index}
              onClick={() => {
                triggerHaptic('light');
                setCurrentIndex(index);
                setEmbedBlocked(false);
              }}
              className={`h-2 rounded-full transition-all duration-300 hover:opacity-100 touch-target btn-touch ${
                index === currentIndex
                  ? `w-8 ${isDark ? 'bg-blue-500' : 'bg-blue-600'}`
                  : `w-2 ${isDark ? 'bg-zinc-600 hover:bg-zinc-500' : 'bg-zinc-300 hover:bg-zinc-400'} opacity-70`
              }`}
              aria-label={`Switch to ${video.label}`}
              title={video.label}
              onTouchStart={() => triggerHaptic('light')}
            />
          ))}
        </div>
      )}

      {/* Video info pills - show all available videos - mobile optimized */}
      {hasMultipleVideos && (
        <div className="flex flex-wrap gap-2 justify-center sm:justify-start px-1">
          {videos.map((video, index) => (
            <button
              key={index}
              onClick={() => {
                triggerHaptic('light');
                setCurrentIndex(index);
                setEmbedBlocked(false);
              }}
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 active:scale-95 touch-target btn-touch card-touch ${
                index === currentIndex
                  ? isDark
                    ? `bg-gradient-to-r ${video.badgeColor} text-white shadow-lg`
                    : `bg-gradient-to-r ${video.badgeColor} text-white shadow-md`
                  : isDark
                    ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                    : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'
              }`}
              onTouchStart={() => triggerHaptic('light')}
            >
              {video.badge}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
